import { Component, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { NzNotificationService } from "ng-zorro-antd/notification";
import type {
    EvidenceModalEmailRequest,
    EvidenceModalLinkedFileRequest,
    EvidenceModalUploadRequest
} from "../evidence_requirements_modal/evidence_requirements_modal.component";
import { EvidenceFileActionsService } from "../../services/evidence_file_actions.service";
import { EvidenceRequirementsService } from "../../services/evidence_requirements.service";
import {
    EvidenceRequirementsLaunchContext,
    EvidenceRequirementsLaunchService
} from "../../services/evidence_requirements_launch.service";
import { ProductLicensesService, TaskAssessmentChoice } from "../../services/product_licenses.service";

@Component({
    selector: "app-evidence-requirements-host",
    templateUrl: "./evidence_requirements_host.component.html"
})
export class EvidenceRequirementsHostComponent implements OnDestroy {
    activeContext: EvidenceRequirementsLaunchContext | null = null;
    assessmentContext: TaskAssessmentChoice | null = null;
    modalVisible = false;
    emailSending = false;
    emailSuccessMessage = "";
    emailFailureMessage = "";

    private readonly stateSubscription: Subscription;
    private contextRequest: Subscription | null = null;
    private emailRequest: Subscription | null = null;
    private fileActionRequests = new Subscription();
    private contextVersion = 0;

    constructor(
        private readonly launcher: EvidenceRequirementsLaunchService,
        private readonly evidenceFileActions: EvidenceFileActionsService,
        private readonly evidenceRequirements: EvidenceRequirementsService,
        private readonly productLicenses: ProductLicensesService,
        private readonly notification: NzNotificationService
    ) {
        this.stateSubscription = this.launcher.watchState().subscribe((state) => {
            this.contextVersion += 1;
            this.contextRequest?.unsubscribe();
            this.contextRequest = null;
            this.emailRequest?.unsubscribe();
            this.emailRequest = null;
            this.emailSending = false;
            this.clearEmailStatus();
            this.fileActionRequests.unsubscribe();
            this.fileActionRequests = new Subscription();
            this.activeContext = null;
            this.assessmentContext = null;
            this.modalVisible = false;

            const context = state.context;
            if (!state.isOpen || !context || !this.launcher.isCurrentContext(context)) return;
            this.activeContext = context;
            if (context.kind === "item") {
                this.modalVisible = true;
                return;
            }

            const version = this.contextVersion;
            this.contextRequest = this.productLicenses.loadTaskAssessmentChoices().subscribe({
                next: (choices) => {
                    if (!this.isCurrentContext(context, version)) return;
                    const selected = choices.find((choice) => choice.id === context.productLicenseId);
                    if (!selected) {
                        this.closeUnavailable();
                        return;
                    }
                    this.assessmentContext = selected;
                    this.modalVisible = true;
                },
                error: () => {
                    if (this.isCurrentContext(context, version)) this.closeUnavailable();
                }
            });
        });
    }

    get productLicenseId(): number | null {
        return this.activeContext?.productLicenseId ?? null;
    }

    get exactEvidenceId(): number | null {
        return this.activeContext?.kind === "item" ? this.activeContext.evidenceId : null;
    }

    get initialItemAction(): "edit" | "delete" | "email" | null {
        const context = this.activeContext;
        return context?.kind === "item" ? context.action || null : null;
    }

    get expectedMerchantId(): number | null {
        return this.activeContext?.kind === "item"
            ? this.activeContext.expectedMerchantId ?? null : null;
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) this.launcher.close();
    }

    onEmailRequested(request: EvidenceModalEmailRequest): void {
        const context = this.activeContext;
        if (!context || !this.modalVisible || this.emailSending) return;

        const itemIds = request?.evidenceRequirementIds;
        if (request?.productLicenseId !== context.productLicenseId || !Array.isArray(itemIds) ||
            !itemIds.length || itemIds.some((id) => !Number.isSafeInteger(id) || id <= 0) ||
            new Set(itemIds).size !== itemIds.length ||
            (context.kind === "item" && (itemIds.length !== 1 || itemIds[0] !== context.evidenceId))) {
            this.emailFailureMessage = "Unable to send evidence requirements for this assessment.";
            return;
        }

        const version = this.contextVersion;
        this.clearEmailStatus();
        this.emailSending = true;
        this.emailRequest = this.evidenceRequirements.sendEvidenceEmail(context.productLicenseId, itemIds).subscribe({
            next: () => {
                if (!this.isCurrentContext(context, version)) return;
                this.emailSending = false;
                this.emailSuccessMessage = "Evidence requirements list sent successfully.";
            },
            error: (error: unknown) => {
                if (!this.isCurrentContext(context, version)) return;
                this.emailSending = false;
                this.emailFailureMessage = this.emailErrorMessage(error);
            }
        });
    }

    clearEmailStatus(): void {
        this.emailSuccessMessage = "";
        this.emailFailureMessage = "";
    }

    onLinkedFileRequested(request: EvidenceModalLinkedFileRequest): void {
        const context = this.activeContext;
        if (!context || !this.modalVisible ||
            (context.kind === "item" && context.evidenceId !== request?.evidenceRequirementId)) return;

        this.fileActionRequests.add(this.evidenceFileActions.openLinkedFile(context.productLicenseId, request, {
            isCurrent: () => this.activeContext === context && this.modalVisible &&
                this.launcher.isCurrentContext(context)
        }));
    }

    onUploadRequested(request: EvidenceModalUploadRequest): void {
        const context = this.activeContext;
        if (!context || !this.modalVisible || request?.productLicenseId !== context.productLicenseId ||
            (context.kind === "item" && context.evidenceId !== request.evidenceRequirementId)) return;

        this.fileActionRequests.add(this.evidenceFileActions.openUpload(request, {
            isCurrent: () => this.activeContext === context && this.modalVisible &&
                this.launcher.isCurrentContext(context)
        }));
    }

    ngOnDestroy(): void {
        this.contextVersion += 1;
        this.contextRequest?.unsubscribe();
        this.emailRequest?.unsubscribe();
        this.fileActionRequests.unsubscribe();
        this.stateSubscription.unsubscribe();
    }

    private isCurrentContext(context: EvidenceRequirementsLaunchContext, version: number): boolean {
        return version === this.contextVersion && this.activeContext === context &&
            this.launcher.isCurrentContext(context);
    }

    private closeUnavailable(): void {
        this.launcher.close();
        this.notification.error("Evidence unavailable", "This assessment is unavailable in your current scope.");
    }

    private emailErrorMessage(error: unknown): string {
        if (error && typeof error === "object") {
            const response = error as { error?: { message?: unknown } };
            const message = response.error?.message;
            if (typeof message === "string" && message.trim()) return message.trim();
        }
        return "Unable to email evidence requirements. Please try again later.";
    }
}
